import { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
	Calendar,
	Shield,
	DollarSign,
	Check,
	CreditCard,
	Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import type { Tool, User } from "@/lib/types";

interface BookingConfirmDialogProps {
	open: boolean;
	onClose: () => void;
	toolId: string;
	startDate: string;
	endDate: string;
	onSuccess: () => void;
}

export function BookingConfirmDialog({
	open,
	onClose,
	toolId,
	startDate,
	endDate,
	onSuccess,
}: BookingConfirmDialogProps) {
	const [tool, setTool] = useState<(Tool & { owner?: User }) | null>(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [confirmed, setConfirmed] = useState(false);
	const [depositAcknowledged, setDepositAcknowledged] = useState(false);

	useEffect(() => {
		if (open && toolId) {
			loadTool();
		}
	}, [open, toolId]);

	async function loadTool() {
		setLoading(true);
		try {
			const data = await api.getTool(toolId);
			setTool(data);
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	}

	const days = Math.max(
		1,
		Math.ceil(
			(new Date(endDate).getTime() - new Date(startDate).getTime()) /
				(1000 * 60 * 60 * 24)
		) + 1
	);
	const rentalCost = tool ? tool.dailyRate * days : 0;
	const deposit = tool?.deposit || 0;
	const total = rentalCost + deposit;

	async function handleConfirm() {
		if (!depositAcknowledged) return;
		setSubmitting(true);
		try {
			await api.createBooking({ toolId, startDate, endDate });
			setConfirmed(true);
			setTimeout(() => {
				onClose();
				onSuccess();
				setConfirmed(false);
				setDepositAcknowledged(false);
			}, 2000);
		} catch (e) {
			console.error("Failed to create booking:", e);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) {
					onClose();
					setConfirmed(false);
					setDepositAcknowledged(false);
				}
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{confirmed ? "Booking Requested!" : "Confirm your rental"}
					</DialogTitle>
					<DialogDescription>
						{confirmed
							? "The lender will review your request."
							: "Review the details and confirm your booking."}
					</DialogDescription>
				</DialogHeader>

				{loading ? (
					<div className="h-48 flex items-center justify-center">
						<div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
					</div>
				) : confirmed ? (
					<div className="text-center py-6">
						<div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
							<Check className="w-7 h-7 text-emerald-600" />
						</div>
						<p className="text-sm text-muted-foreground">
							You'll be notified when the lender responds.
						</p>
					</div>
				) : tool ? (
					<div className="space-y-4">
						<div className="p-3 bg-muted/50 rounded-lg">
							<p className="font-medium text-sm">{tool.name}</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								by {tool.owner?.name}
							</p>
						</div>

						<div className="space-y-2">
							<div className="flex items-center gap-2 text-sm">
								<Calendar className="w-4 h-4 text-muted-foreground" />
								<span>
									{startDate} to {endDate}
								</span>
								<Badge variant="secondary" className="text-xs">
									{days} day{days > 1 ? "s" : ""}
								</Badge>
							</div>
						</div>

						<Separator />

						<div className="space-y-2">
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">
									Rental ({days} day{days > 1 ? "s" : ""} x ${tool.dailyRate})
								</span>
								<span>${rentalCost}</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground flex items-center gap-1">
									<Shield className="w-3 h-3" />
									Security deposit
								</span>
								<span>${deposit}</span>
							</div>
							<Separator />
							<div className="flex justify-between font-semibold">
								<span>Total</span>
								<span>${total}</span>
							</div>
						</div>

						{/* Deposit acknowledgment */}
						<label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
							<input
								type="checkbox"
								checked={depositAcknowledged}
								onChange={(e) => setDepositAcknowledged(e.target.checked)}
								className="mt-0.5"
							/>
							<div className="text-xs">
								<p className="font-medium">I understand the deposit terms</p>
								<p className="text-muted-foreground mt-0.5">
									The ${deposit} deposit will be held securely and returned when
									the tool is returned in the same condition. Deductions may
									apply for damage.
								</p>
							</div>
						</label>

						<div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded-md">
							<Lock className="w-3.5 h-3.5 shrink-0" />
							Payment processed securely. Funds held until rental begins.
						</div>

						<div className="flex gap-2">
							<Button variant="outline" className="flex-1" onClick={onClose}>
								Cancel
							</Button>
							<Button
								className="flex-1 gap-2"
								disabled={!depositAcknowledged || submitting}
								onClick={handleConfirm}
							>
								<CreditCard className="w-4 h-4" />
								{submitting ? "Processing..." : `Pay $${total}`}
							</Button>
						</div>
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	);
}
